import { Suspense } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { SidebarManager } from '@/common/components/sidebar-manager/SidebarManager';
import { WizardStepIndicator } from '@/features/supply-points/components/create-wizard/WizardStepIndicator';
import { CreateSupplyPointStep1 } from '@/features/supply-points/components/create-wizard/CreateSupplyPointStep1';
import { CreateSupplyPointStep2 } from '@/features/supply-points/components/create-wizard/CreateSupplyPointStep2';
import { CreateSupplyPointStep3 } from '@/features/supply-points/components/create-wizard/CreateSupplyPointStep3';
import { CreateSupplyPointStep4 } from '@/features/supply-points/components/create-wizard/CreateSupplyPointStep4';

interface CreateSupplyPointPageProps {
  step: number;
  id?: number;
  orgId?: number;
}

export function CreateSupplyPointPage({ step, id, orgId }: CreateSupplyPointPageProps) {
  const navigate = useNavigate();

  const goToStep = (newStep: number, supplyPointId?: number) => {
    void navigate({
      to: '/supply-points/new',
      search: {
        step: newStep,
        ...(supplyPointId !== undefined ? { id: supplyPointId } : id !== undefined ? { id } : {}),
        ...(orgId ? { orgId } : {}),
      },
    });
  };

  const handleCreated = (newId: number, name: string) => {
    toast.success(t`Supply point "${name}" created successfully.`);
    goToStep(2, newId);
  };
  const handleStep2Next = () => goToStep(3);
  const handleStep3Next = () => goToStep(4);

  const handleBack = () => {
    if (step === 1) {
      void navigate({ to: '/no-supply-points' });
    } else {
      goToStep(step - 1);
    }
  };

  return (
    <SidebarManager>
      <div className="max-w-2xl pb-12">
        {/* Back */}
        <button
          onClick={handleBack}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft size={15} />
          <Trans>Back</Trans>
        </button>

        {/* Step indicator */}
        <div className="mb-6">
          <WizardStepIndicator current={step} />
        </div>

        {/* Step content */}
        {step === 1 && (
          <Suspense>
            <CreateSupplyPointStep1
              orgId={orgId}
              onCreated={handleCreated}
              onCancel={() => void navigate({ to: '/no-supply-points' })}
            />
          </Suspense>
        )}

        {step === 2 && id !== undefined && (
          <Suspense>
            <CreateSupplyPointStep2 supplyPointId={id} onNext={handleStep2Next} />
          </Suspense>
        )}

        {step === 3 && id !== undefined && (
          <Suspense>
            <CreateSupplyPointStep3 supplyPointId={id} onNext={handleStep3Next} />
          </Suspense>
        )}

        {step === 4 && id !== undefined && (
          <Suspense>
            <CreateSupplyPointStep4 supplyPointId={id} />
          </Suspense>
        )}
      </div>
    </SidebarManager>
  );
}
